import React, { useEffect, useMemo, useRef, useState } from 'react';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import { useDataProvider, useListController, useNotify, useRefresh } from 'react-admin';

import Pagination from 'components/pagination/Pagination';
import ItemCard from '../item-card';
import { RewardedActionVerificationDto } from '../../types';
import EmptyBoxIcon from 'assets/icons/empty-box.svg';
import SelectionBar from './SelectionBar';
import BulkDeclineDialog from './BulkDeclineDialog';

const useStyles = makeStyles((theme) => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(262px, 1fr))',
    gap: '16px',
    justifyItems: 'center',
  },
  empty: {
    padding: theme.spacing(8, 0),
    color: theme.palette.text.secondary,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 360,
  },
  pagination: {
    marginTop: theme.spacing(3),
  },
}));

type ListViewProps = {
  status: string;
  search?: string;
  onStatusChange?: () => void;
};

const getNameFromEmail = (email?: string) => {
  if (!email) return undefined;
  return email.split('@')[0];
};

const RewardsVerificationListView = ({
  status,
  search,
  onStatusChange,
  ...props
}: ListViewProps & any) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeclineOpen, setBulkDeclineOpen] = useState(false);
  const [bulkDeclineSubmitting, setBulkDeclineSubmitting] = useState(false);
  const prevPendingTotalRef = useRef<number | null>(null);

  const handleRefreshAndCount = () => {
    refresh();
    onStatusChange?.();
  };

  const filters = useMemo(
    () => ({
      status,
      ...(search ? { search } : {}),
    }),
    [status, search]
  );

  const controllerProps = useListController({
    ...props,
    resource: 'rewardsVerificationList',
    basePath: props?.basePath || '/rewardsVerification',
    perPage: props?.perPage || 10,
    sort: { field: 'createdAt', order: 'DESC' },
    filter: filters,
  });

  const {
    ids = [],
    data = {},
    page,
    perPage,
    setPage,
    setPerPage,
    total = 0,
    loading,
  } = controllerProps;

  const items: RewardedActionVerificationDto[] = ids
    .map((id) => data[id])
    .filter(Boolean) as RewardedActionVerificationDto[];

  useEffect(() => {
    setSelectedIds([]);
  }, [ids]);

  // Note: this refreshes the global pending count even when the list is filtered.
  useEffect(() => {
    if (status !== 'PENDING') {
      prevPendingTotalRef.current = null;
      return;
    }

    if (loading) {
      return;
    }

    if (prevPendingTotalRef.current === null) {
      prevPendingTotalRef.current = total;
      return;
    }

    if (prevPendingTotalRef.current !== total) {
      prevPendingTotalRef.current = total;
      onStatusChange?.();
    }
  }, [status, loading, total, onStatusChange]);

  const toggleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(items.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelect = (id: string) => () => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    );
  };

  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const showSelection = items.length > 0 && !loading && status === 'PENDING';
  const hasSelection = selectedIds.length > 0;

  const handleBulkApprove = async () => {
    if (!hasSelection) return;

    try {
      await dataProvider.post('rewardsBulkUpdateVerificationStatus', {
        data: { status: 'APPROVED', ids: selectedIds },
      });
      setSelectedIds([]);
      handleRefreshAndCount();
    } catch (error: any) {
      const message =
        typeof error === 'string' ? error : error?.message || 'Failed to approve items';
      notify(message, 'warning');
    }
  };

  const handleOpenBulkDecline = () => setBulkDeclineOpen(true);
  const handleCloseBulkDecline = () => setBulkDeclineOpen(false);

  const handleSubmitBulkDecline = async ({ note }) => {
    if (!hasSelection) return;

    try {
      setBulkDeclineSubmitting(true);
      await dataProvider.post('rewardsBulkUpdateVerificationStatus', {
        data: { status: 'REJECTED', note, ids: selectedIds },
      });
      setBulkDeclineOpen(false);
      setSelectedIds([]);
      handleRefreshAndCount();
    } catch (error: any) {
      const message =
        typeof error === 'string' ? error : error?.message || 'Failed to decline items';
      notify(message, 'warning');
    } finally {
      setBulkDeclineSubmitting(false);
    }
  };

  return (
    <Box>
      {showSelection && (
        <SelectionBar
          allSelected={allSelected}
          hasSelection={hasSelection}
          selectedCount={selectedIds.length}
          onToggleAll={toggleSelectAll}
          onBulkDecline={handleOpenBulkDecline}
          onBulkApprove={handleBulkApprove}
        />
      )}

      {loading ? (
        <CircularProgress />
      ) : items.length === 0 ? (
        <Box className={classes.emptyContainer}>
          <Box className={classes.empty}>
            <img src={EmptyBoxIcon} alt="Empty" width={48} height={48} />
            <Typography>No items yet</Typography>
          </Box>
        </Box>
      ) : (
        <Box className={classes.grid}>
          {items.map((item) => (
            <ItemCard
              key={item.id}
              id={item.id}
              title={item.rewardedActionTitle}
              name={getNameFromEmail(item.tenantEmail)}
              email={item.tenantEmail}
              screenshotUrl={item.screenshotUrl}
              status={item.status}
              selected={selectedIds.includes(item.id)}
              onSelectToggle={toggleSelect(item.id)}
              onStatusChange={handleRefreshAndCount}
            />
          ))}
        </Box>
      )}

      {items.length > 0 && (
        <Box className={classes.pagination}>
          <Pagination
            page={page}
            perPage={perPage}
            setPage={setPage}
            setPerPage={setPerPage}
            total={total}
            loading={loading}
          />
        </Box>
      )}

      {bulkDeclineOpen && (
        <BulkDeclineDialog
          open={bulkDeclineOpen}
          onClose={handleCloseBulkDecline}
          onSubmit={handleSubmitBulkDecline}
          submitting={bulkDeclineSubmitting}
          hasSelection={hasSelection}
        />
      )}
    </Box>
  );
};

export default RewardsVerificationListView;
